import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CheckCircle2, Circle, Loader2, Save, Plus, X } from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import Brand from "@/components/Brand";

// Family CFO Financial Snapshot — public multi-step intake.
// Slice 1 has sections 1-3 fully wired; 4-10 are placeholders showing
// "Coming soon" so staff can drive traffic today.

const SECTIONS = [
    { id: 1, key: "household", title: "Household details" },
    { id: 2, key: "goals", title: "Family goals" },
    { id: 3, key: "income", title: "Employment & income" },
    { id: 4, key: "living_arrangements", title: "Living arrangements" },
    { id: 5, key: "assets", title: "Assets & savings" },
    { id: 6, key: "living_costs", title: "Weekly living costs" },
    { id: 7, key: "debts", title: "Debt register" },
    { id: 8, key: "arrears", title: "Arrears & urgent payments" },
    { id: 9, key: "documents", title: "Supporting documents", stub: true },
    { id: 10, key: "consent", title: "Consent & declaration", stub: true },
];

export default function FamilyCfoIntake() {
    const [search, setSearch] = useSearchParams();
    const navigate = useNavigate();

    // Persist intake_id in localStorage so a client can close and come back.
    const [intakeId, setIntakeId] = useState(() => search.get("intake_id") || localStorage.getItem("familyCfoIntakeId") || null);
    const [intake, setIntake] = useState(null);
    const [current, setCurrent] = useState(1);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                if (!intakeId) {
                    const { data } = await api.post("/family-cfo/intake/start", { source: "landing" });
                    setIntakeId(data.intake_id);
                    localStorage.setItem("familyCfoIntakeId", data.intake_id);
                    setSearch({ intake_id: data.intake_id }, { replace: true });
                }
                const id = intakeId || localStorage.getItem("familyCfoIntakeId");
                if (id) {
                    const { data: row } = await api.get(`/family-cfo/intake/${id}`);
                    setIntake(row);
                }
            } catch (e) {
                toast.error(formatApiError(e));
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [intakeId]);

    async function reload() {
        if (!intakeId) return;
        const { data: row } = await api.get(`/family-cfo/intake/${intakeId}`);
        setIntake(row);
    }

    async function saveSection(sectionKey, payload) {
        if (!intakeId) return;
        setSaving(true);
        try {
            const { data: row } = await api.patch(`/family-cfo/intake/${intakeId}/${sectionKey}`, payload);
            setIntake(row);
            toast.success("Saved");
            return row;
        } catch (e) {
            toast.error(formatApiError(e));
            throw e;
        } finally {
            setSaving(false);
        }
    }

    const activeSection = SECTIONS.find((s) => s.id === current);
    const progress = intake?.progress_percent ?? 0;

    return (
        <div className="min-h-screen bg-[#FBFAF6] text-[#0F1115]" data-testid="family-cfo-intake">
            <header className="border-b border-[#EAE7DE] bg-white/70 backdrop-blur">
                <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
                    <Brand />
                    <button onClick={() => navigate("/family-cfo")} className="text-sm text-[#5E636E] hover:text-[#0F1115]" data-testid="intake-exit">
                        Save & exit
                    </button>
                </div>
            </header>

            <div className="mx-auto max-w-6xl px-5 py-8 sm:py-12">
                <div className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Family CFO Financial Snapshot</div>
                <h1 className="mt-2 font-display text-3xl sm:text-4xl font-semibold tracking-tight">Let&apos;s build your household plan</h1>
                <p className="mt-2 text-sm text-[#5E636E]">
                    Autosaves as you go. Pause anytime — we&apos;ll pick up where you left off.
                </p>

                {/* Progress bar */}
                <div className="mt-6 flex items-center gap-3">
                    <div className="h-1.5 flex-1 rounded-full bg-[#EAE7DE] overflow-hidden">
                        <div className="h-full bg-[#0E3B2E] transition-all" style={{ width: `${progress}%` }} data-testid="intake-progress" />
                    </div>
                    <div className="text-xs text-[#5E636E] w-10 text-right">{progress}%</div>
                </div>

                {loading ? (
                    <div className="mt-14 text-center text-sm text-[#5E636E]">
                        <Loader2 className="inline h-4 w-4 animate-spin mr-2" /> Loading your snapshot…
                    </div>
                ) : (
                    <div className="mt-8 grid gap-8 lg:grid-cols-[240px_1fr]">
                        {/* Section nav */}
                        <nav className="space-y-1">
                            {SECTIONS.map((s) => {
                                const done = isSectionDone(s, intake);
                                return (
                                    <button
                                        key={s.id}
                                        onClick={() => setCurrent(s.id)}
                                        className={
                                            "w-full text-left px-3 py-2 rounded-lg text-sm flex items-start gap-2 " +
                                            (current === s.id ? "bg-[#0E3B2E]/8 text-[#0F1115]" : "text-[#3A3D45] hover:bg-[#F1EDE2]")
                                        }
                                        data-testid={`intake-nav-${s.id}`}
                                    >
                                        {done
                                            ? <CheckCircle2 className="h-4 w-4 mt-0.5 text-[#0E3B2E]" />
                                            : <Circle className="h-4 w-4 mt-0.5 text-[#B7B3A6]" />}
                                        <span><span className="text-[#5E636E] mr-1.5">{s.id}.</span>{s.title}</span>
                                    </button>
                                );
                            })}
                        </nav>

                        {/* Active section */}
                        <div className="rounded-3xl border border-[#EAE7DE] bg-white p-6 sm:p-8 min-h-[400px]">
                            <div className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Section {activeSection.id} of {SECTIONS.length}</div>
                            <h2 className="mt-1 font-display text-2xl font-semibold tracking-tight">{activeSection.title}</h2>
                            <div className="mt-6">
                                {activeSection.stub
                                    ? <StubPanel section={activeSection} />
                                    : current === 1
                                        ? <HouseholdSection intake={intake} onSave={(p) => saveSection("household", p)} saving={saving} />
                                        : current === 2
                                            ? <GoalsSection intake={intake} onSave={(p) => saveSection("goals", p)} saving={saving} />
                                            : current === 3
                                                ? <IncomeSection intake={intake} onSave={(p) => saveSection("income", p)} onReload={reload} saving={saving} />
                                                : current === 4
                                                    ? <LivingArrangementsSection intake={intake} onSave={(p) => saveSection("living_arrangements", p)} saving={saving} />
                                                    : current === 5
                                                        ? <AssetsSection intake={intake} onSave={(p) => saveSection("assets", p)} saving={saving} />
                                                        : current === 6
                                                            ? <LivingCostsSection intake={intake} onSave={(p) => saveSection("living_costs", p)} saving={saving} />
                                                            : current === 7
                                                                ? <DebtsSection intake={intake} onSave={(p) => saveSection("debts", p)} saving={saving} />
                                                                : <ArrearsSection intake={intake} onSave={(p) => saveSection("arrears", p)} saving={saving} />}
                            </div>

                            {/* Nav footer */}
                            <div className="mt-8 flex items-center justify-between">
                                <Button variant="outline" disabled={current === 1} onClick={() => setCurrent((n) => Math.max(1, n - 1))} className="gap-1.5" data-testid="intake-prev">
                                    <ArrowLeft className="h-4 w-4" /> Previous
                                </Button>
                                <Button disabled={current === SECTIONS.length} onClick={() => setCurrent((n) => Math.min(SECTIONS.length, n + 1))} className="bg-[#0E3B2E] hover:bg-[#0B1F17] text-white gap-1.5" data-testid="intake-next">
                                    Next <ArrowRight className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function StubPanel({ section }) {
    return (
        <div className="rounded-2xl border border-dashed border-[#DCD8CC] bg-[#FBFAF6] p-6 text-sm text-[#5E636E]">
            <p><b>Coming next.</b> This section — {section.title.toLowerCase()} — will be enabled shortly.
            You can complete Sections 1–3 now and we&apos;ll email you when the rest is ready.</p>
        </div>
    );
}

// ---------------- Section 1: Household ----------------
function HouseholdSection({ intake, onSave, saving }) {
    const initial = intake?.household || {};
    const [form, setForm] = useState({
        client_first_name: initial.client_first_name || "",
        client_last_name: initial.client_last_name || "",
        client_dob: initial.client_dob || "",
        mobile: initial.mobile || "",
        email: initial.email || "",
        residential_address: initial.residential_address || "",
        preferred_contact: initial.preferred_contact || "Email",
        partner_full_name: initial.partner_full_name || "",
        partner_age: initial.partner_age ?? "",
        partner_relationship: initial.partner_relationship || "",
        partner_employed: initial.partner_employed ?? null,
        partner_mobile: initial.partner_mobile || "",
        partner_email: initial.partner_email || "",
        dependants: initial.dependants || [],
    });

    const financialDeps = form.dependants.filter((d) => d.financially_dependent).length;
    const householdSize = 1 + (form.partner_full_name ? 1 : 0) + financialDeps;

    function addDependant() {
        setForm((f) => ({ ...f, dependants: [...f.dependants, { name: "", age: "", relationship: "", financially_dependent: true, notes: "" }] }));
    }
    function removeDependant(idx) {
        setForm((f) => ({ ...f, dependants: f.dependants.filter((_, i) => i !== idx) }));
    }
    function setDep(idx, patch) {
        setForm((f) => ({ ...f, dependants: f.dependants.map((d, i) => i === idx ? { ...d, ...patch } : d) }));
    }

    async function handleSave(e) {
        e.preventDefault();
        const payload = {
            ...form,
            partner_age: form.partner_age === "" ? null : Number(form.partner_age),
            dependants: form.dependants.map((d) => ({
                ...d, age: d.age === "" || d.age == null ? null : Number(d.age),
            })),
        };
        await onSave(payload);
    }

    return (
        <form onSubmit={handleSave} className="space-y-6">
            <fieldset className="space-y-3">
                <legend className="text-[13px] font-semibold">Main client</legend>
                <div className="grid grid-cols-2 gap-3">
                    <Field label="First name" required value={form.client_first_name} onChange={(v) => setForm({ ...form, client_first_name: v })} testId="hh-first" />
                    <Field label="Last name" required value={form.client_last_name} onChange={(v) => setForm({ ...form, client_last_name: v })} testId="hh-last" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <Field label="Date of birth" type="date" value={form.client_dob} onChange={(v) => setForm({ ...form, client_dob: v })} testId="hh-dob" />
                    <Field label="Mobile" required value={form.mobile} onChange={(v) => setForm({ ...form, mobile: v })} testId="hh-mobile" />
                </div>
                <Field label="Email" required type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="hh-email" />
                <Field label="Residential address" value={form.residential_address} onChange={(v) => setForm({ ...form, residential_address: v })} testId="hh-addr" />
                <SelectField label="Preferred contact" value={form.preferred_contact} onChange={(v) => setForm({ ...form, preferred_contact: v })} options={["Email", "Phone", "Text"]} testId="hh-contact" />
            </fieldset>

            <fieldset className="space-y-3 pt-4 border-t border-[#EAE7DE]">
                <legend className="text-[13px] font-semibold">Partner or spouse <span className="font-normal text-[#5E636E]">(optional)</span></legend>
                <div className="grid grid-cols-2 gap-3">
                    <Field label="Full name" value={form.partner_full_name} onChange={(v) => setForm({ ...form, partner_full_name: v })} testId="hh-partner-name" />
                    <Field label="Age" type="number" value={form.partner_age} onChange={(v) => setForm({ ...form, partner_age: v })} testId="hh-partner-age" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <Field label="Relationship" value={form.partner_relationship} onChange={(v) => setForm({ ...form, partner_relationship: v })} testId="hh-partner-rel" placeholder="Spouse, partner, etc." />
                    <SelectField label="Employed?" value={form.partner_employed === true ? "Yes" : form.partner_employed === false ? "No" : ""} onChange={(v) => setForm({ ...form, partner_employed: v === "Yes" ? true : v === "No" ? false : null })} options={["", "Yes", "No"]} testId="hh-partner-emp" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <Field label="Partner mobile" value={form.partner_mobile} onChange={(v) => setForm({ ...form, partner_mobile: v })} testId="hh-partner-mobile" />
                    <Field label="Partner email" type="email" value={form.partner_email} onChange={(v) => setForm({ ...form, partner_email: v })} testId="hh-partner-email" />
                </div>
            </fieldset>

            <fieldset className="space-y-3 pt-4 border-t border-[#EAE7DE]">
                <div className="flex items-center justify-between">
                    <legend className="text-[13px] font-semibold">Dependants</legend>
                    <Button type="button" variant="outline" size="sm" onClick={addDependant} className="gap-1.5" data-testid="hh-add-dep">
                        <Plus className="h-3.5 w-3.5" /> Add dependant
                    </Button>
                </div>
                {form.dependants.length === 0 && (
                    <div className="text-sm text-[#5E636E] italic">No dependants added yet.</div>
                )}
                {form.dependants.map((d, i) => (
                    <div key={i} className="rounded-xl border border-[#EAE7DE] p-3 space-y-2 relative">
                        <button type="button" className="absolute top-2 right-2 text-[#B7B3A6] hover:text-[#0F1115]" onClick={() => removeDependant(i)} aria-label="Remove">
                            <X className="h-4 w-4" />
                        </button>
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Name" value={d.name} onChange={(v) => setDep(i, { name: v })} testId={`hh-dep-${i}-name`} />
                            <Field label="Age" type="number" value={d.age} onChange={(v) => setDep(i, { age: v })} testId={`hh-dep-${i}-age`} />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Relationship" value={d.relationship} onChange={(v) => setDep(i, { relationship: v })} testId={`hh-dep-${i}-rel`} placeholder="Child, parent, other" />
                            <SelectField label="Financially dependent?" value={d.financially_dependent ? "Yes" : "No"} onChange={(v) => setDep(i, { financially_dependent: v === "Yes" })} options={["Yes", "No"]} testId={`hh-dep-${i}-fd`} />
                        </div>
                    </div>
                ))}
            </fieldset>

            <div className="rounded-xl bg-[#F1EDE2] p-4 text-sm">
                <div className="flex items-center justify-between">
                    <div>Total household members</div>
                    <div className="font-semibold">{householdSize}</div>
                </div>
                <div className="flex items-center justify-between mt-1">
                    <div>Financial dependants</div>
                    <div className="font-semibold">{financialDeps}</div>
                </div>
            </div>

            <div className="pt-2">
                <Button type="submit" disabled={saving} className="bg-[#0F1115] hover:bg-[#2E2E2E] text-white gap-2" data-testid="hh-save">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    {saving ? "Saving…" : "Save section"}
                </Button>
            </div>
        </form>
    );
}

// ---------------- Section 2: Goals ----------------
const REASON_OPTIONS = [
    "We earn good income but still feel broke",
    "We have credit card debt",
    "We have personal loans",
    "We have car finance",
    "We are behind on bills",
    "We want a proper household budget",
    "We want to reduce debt",
    "We want to start saving consistently",
    "We want to stop using credit cards",
    "We want to plan for school costs",
    "We want to plan for travel or family holidays",
    "We want to build an emergency fund",
    "We want someone to guide us month by month",
    "Other",
];

const PRIORITY_OPTIONS = [
    "", "Reduce debt", "Catch up overdue bills", "Build emergency savings",
    "Control spending", "Prepare for a house deposit", "Prepare for school costs",
    "Prepare for family travel", "Improve household cashflow", "Other",
];

function GoalsSection({ intake, onSave, saving }) {
    const initial = intake?.goals || {};
    const [form, setForm] = useState({
        reasons: initial.reasons || [],
        current_situation: initial.current_situation || "",
        twelve_month_success: initial.twelve_month_success || "",
        biggest_stress: initial.biggest_stress || "",
        main_priority: initial.main_priority || "",
    });

    function toggleReason(r) {
        setForm((f) => ({ ...f, reasons: f.reasons.includes(r) ? f.reasons.filter((x) => x !== r) : [...f.reasons, r] }));
    }

    async function submit(e) {
        e.preventDefault();
        await onSave(form);
    }

    return (
        <form onSubmit={submit} className="space-y-6">
            <div>
                <Label className="text-[13px] font-semibold">What made you enquire about the Family CFO Plan?</Label>
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                    {REASON_OPTIONS.map((r, i) => (
                        <label key={r} className="flex items-start gap-2 rounded-lg p-2 hover:bg-[#F1EDE2] cursor-pointer text-sm">
                            <input type="checkbox" checked={form.reasons.includes(r)} onChange={() => toggleReason(r)} className="mt-1" data-testid={`goal-reason-${i}`} />
                            <span>{r}</span>
                        </label>
                    ))}
                </div>
            </div>

            <div>
                <Label>Briefly explain what is currently happening with your household finances.</Label>
                <Textarea rows={3} value={form.current_situation} onChange={(e) => setForm({ ...form, current_situation: e.target.value })} data-testid="goal-current" className="mt-1" />
            </div>
            <div>
                <Label>What would success look like for your family in 12 months?</Label>
                <Textarea rows={3} value={form.twelve_month_success} onChange={(e) => setForm({ ...form, twelve_month_success: e.target.value })} data-testid="goal-success" className="mt-1" />
            </div>
            <div>
                <Label>What is your biggest money stress right now?</Label>
                <Textarea rows={2} value={form.biggest_stress} onChange={(e) => setForm({ ...form, biggest_stress: e.target.value })} data-testid="goal-stress" className="mt-1" />
            </div>

            <SelectField label="Main priority" value={form.main_priority} onChange={(v) => setForm({ ...form, main_priority: v })} options={PRIORITY_OPTIONS} testId="goal-priority" />

            <div>
                <Button type="submit" disabled={saving} className="bg-[#0F1115] hover:bg-[#2E2E2E] text-white gap-2" data-testid="goal-save">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    {saving ? "Saving…" : "Save section"}
                </Button>
            </div>
        </form>
    );
}

// ---------------- Section 3: Income ----------------
const INCOME_KINDS = [
    "Salary or wages", "Commission", "Self-employed income", "Pension or superannuation",
    "Benefit", "Child support", "ACC", "Rental or board income", "Interest or dividends", "Other income",
];
const FREQ_OPTIONS = ["weekly", "fortnightly", "monthly", "irregular"];

function toWeekly(amount, freq) {
    const a = Number(amount) || 0;
    if (freq === "weekly") return a;
    if (freq === "fortnightly") return a / 2;
    if (freq === "monthly" || freq === "irregular") return (a * 12) / 52;
    return a;
}

function IncomeItems({ items, setItems, label, testIdPrefix }) {
    function add() {
        setItems([...items, { kind: INCOME_KINDS[0], amount: "", frequency: "weekly" }]);
    }
    function update(i, patch) {
        setItems(items.map((it, idx) => idx === i ? { ...it, ...patch } : it));
    }
    function remove(i) {
        setItems(items.filter((_, idx) => idx !== i));
    }
    const weeklyTotal = items.reduce((s, it) => s + toWeekly(it.amount, it.frequency), 0);
    return (
        <div className="rounded-xl border border-[#EAE7DE] p-4 space-y-3">
            <div className="flex items-center justify-between">
                <div className="text-[13px] font-semibold">{label}</div>
                <Button type="button" size="sm" variant="outline" onClick={add} className="gap-1.5" data-testid={`${testIdPrefix}-add`}>
                    <Plus className="h-3.5 w-3.5" /> Add income
                </Button>
            </div>
            {items.length === 0 && <div className="text-sm text-[#5E636E] italic">No income sources yet.</div>}
            {items.map((it, i) => (
                <div key={i} className="grid grid-cols-[1fr_120px_120px_28px] gap-2 items-end">
                    <SelectField label={i === 0 ? "Type" : ""} value={it.kind} onChange={(v) => update(i, { kind: v })} options={INCOME_KINDS} testId={`${testIdPrefix}-${i}-kind`} />
                    <Field label={i === 0 ? "Amount (NZD)" : ""} type="number" value={it.amount} onChange={(v) => update(i, { amount: v })} testId={`${testIdPrefix}-${i}-amt`} />
                    <SelectField label={i === 0 ? "Frequency" : ""} value={it.frequency} onChange={(v) => update(i, { frequency: v })} options={FREQ_OPTIONS} testId={`${testIdPrefix}-${i}-freq`} />
                    <button type="button" onClick={() => remove(i)} className="h-9 text-[#B7B3A6] hover:text-[#0F1115]" aria-label="Remove">
                        <X className="h-4 w-4" />
                    </button>
                </div>
            ))}
            <div className="text-[13px] text-[#5E636E] pt-1">
                Weekly total: <span className="font-semibold text-[#0F1115]">${weeklyTotal.toFixed(2)}</span>
            </div>
        </div>
    );
}

function IncomeSection({ intake, onSave, onReload, saving }) {
    const initial = intake?.income || {};
    const [clientItems, setClientItems] = useState(initial.client_items || []);
    const [partnerItems, setPartnerItems] = useState(initial.partner_items || []);

    const totals = useMemo(() => {
        const c = clientItems.reduce((s, it) => s + toWeekly(it.amount, it.frequency), 0);
        const p = partnerItems.reduce((s, it) => s + toWeekly(it.amount, it.frequency), 0);
        return { client: c, partner: p, household: c + p, monthlyEst: ((c + p) * 52) / 12 };
    }, [clientItems, partnerItems]);

    async function submit(e) {
        e.preventDefault();
        const payload = {
            client_items: clientItems.map((it) => ({ ...it, amount: Number(it.amount) || 0 })),
            partner_items: partnerItems.map((it) => ({ ...it, amount: Number(it.amount) || 0 })),
        };
        await onSave(payload);
        await onReload();
    }

    return (
        <form onSubmit={submit} className="space-y-4">
            <IncomeItems items={clientItems} setItems={setClientItems} label="Client income" testIdPrefix="inc-client" />
            <IncomeItems items={partnerItems} setItems={setPartnerItems} label="Partner income" testIdPrefix="inc-partner" />

            <div className="rounded-xl bg-[#0E3B2E]/8 p-4 text-sm space-y-1">
                <Row label="Client weekly" value={`$${totals.client.toFixed(2)}`} />
                <Row label="Partner weekly" value={`$${totals.partner.toFixed(2)}`} />
                <Row label="Total weekly household income" value={`$${totals.household.toFixed(2)}`} bold />
                <Row label="Estimated monthly household income" value={`$${totals.monthlyEst.toFixed(2)}`} />
            </div>

            <div>
                <Button type="submit" disabled={saving} className="bg-[#0F1115] hover:bg-[#2E2E2E] text-white gap-2" data-testid="inc-save">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    {saving ? "Saving…" : "Save section"}
                </Button>
            </div>
        </form>
    );
}

// ---------------- Shared field helpers ----------------
function Field({ label, value, onChange, type = "text", required, placeholder, testId }) {
    return (
        <div>
            {label ? <Label className="text-[12px] text-[#5E636E]">{label}{required && <span className="text-red-600 ml-0.5">*</span>}</Label> : null}
            <Input required={required} type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={label ? "mt-1" : ""} data-testid={testId} />
        </div>
    );
}

function SelectField({ label, value, onChange, options, testId }) {
    return (
        <div>
            {label ? <Label className="text-[12px] text-[#5E636E]">{label}</Label> : null}
            <select
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                className={"w-full rounded-md border border-[#E4E4E7] bg-white px-3 h-9 text-sm " + (label ? "mt-1" : "")}
                data-testid={testId}
            >
                {options.map((o) => <option key={o} value={o}>{o || "—"}</option>)}
            </select>
        </div>
    );
}

function Row({ label, value, bold }) {
    return (
        <div className="flex items-center justify-between">
            <div className={bold ? "font-medium" : ""}>{label}</div>
            <div className={"tabular-nums " + (bold ? "font-semibold" : "")}>{value}</div>
        </div>
    );
}


// ---------------- Section-done helper (nav ticks) ----------------
function isSectionDone(section, intake) {
    if (!intake) return false;
    switch (section.key) {
        case "household": return !!intake.household?.email;
        case "goals": return !!(intake.goals?.reasons?.length || intake.goals?.main_priority);
        case "income": return !!(intake.income?.client_items?.length || intake.income?.partner_items?.length);
        case "living_arrangements": return !!intake.living_arrangements?.arrangement;
        case "assets": return !!intake.assets?.items?.length;
        case "living_costs": return !!intake.living_costs?.items?.length;
        case "debts": return !!(intake.debts?.items?.length || intake.debts?.no_debts);
        case "arrears": return !!intake.arrears?.reviewed;
        case "documents": return (intake.documents_uploaded_count || 0) > 0;
        case "consent": return !!intake.consent_signed_at;
        default: return false;
    }
}

// ---------------- Section 4: Living arrangements ----------------
const ARRANGEMENTS = [
    { v: "own_mortgage", l: "Own home with mortgage" },
    { v: "renting", l: "Renting" },
    { v: "boarding", l: "Boarding" },
    { v: "flatting", l: "Flatting" },
    { v: "family", l: "Living with family" },
    { v: "other", l: "Other" },
];

function LivingArrangementsSection({ intake, onSave, saving }) {
    const initial = intake?.living_arrangements || {};
    const [form, setForm] = useState({
        arrangement: initial.arrangement || "renting",
        weekly_amount: initial.weekly_amount ?? "",
        arrears: initial.arrears ?? "",
        frequency: initial.frequency || "weekly",
        provider_name: initial.provider_name || "",
        next_due_date: initial.next_due_date || "",
        notes: initial.notes || "",
    });

    const householdWeekly = intake?.derived?.income?.household_weekly || 0;
    const housingWk = toWeekly(form.weekly_amount || 0, form.frequency);
    const ratio = householdWeekly > 0 ? housingWk / householdWeekly : null;
    const pressure = ratio !== null && ratio > 0.35;

    async function submit(e) {
        e.preventDefault();
        await onSave({
            ...form,
            weekly_amount: Number(form.weekly_amount) || 0,
            arrears: Number(form.arrears) || 0,
        });
    }

    return (
        <form onSubmit={submit} className="space-y-5">
            <div>
                <Label className="text-[13px] font-semibold">What is your current living arrangement?</Label>
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                    {ARRANGEMENTS.map((a, i) => (
                        <label key={a.v} className="flex items-start gap-2 rounded-lg p-2 hover:bg-[#F1EDE2] cursor-pointer text-sm">
                            <input type="radio" name="arrangement" checked={form.arrangement === a.v} onChange={() => setForm({ ...form, arrangement: a.v })} className="mt-1" data-testid={`la-arrangement-${i}`} />
                            <span>{a.l}</span>
                        </label>
                    ))}
                </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
                <Field label="Weekly rent, mortgage or board (NZD)" type="number" value={form.weekly_amount} onChange={(v) => setForm({ ...form, weekly_amount: v })} testId="la-amt" />
                <SelectField label="Payment frequency" value={form.frequency} onChange={(v) => setForm({ ...form, frequency: v })} options={FREQ_OPTIONS} testId="la-freq" />
            </div>
            <div className="grid grid-cols-2 gap-3">
                <Field label="Arrears if any (NZD)" type="number" value={form.arrears} onChange={(v) => setForm({ ...form, arrears: v })} testId="la-arrears" />
                <Field label="Next payment due" type="date" value={form.next_due_date} onChange={(v) => setForm({ ...form, next_due_date: v })} testId="la-due" />
            </div>
            <Field label="Landlord / lender / person paid" value={form.provider_name} onChange={(v) => setForm({ ...form, provider_name: v })} testId="la-provider" />
            <div>
                <Label>Notes</Label>
                <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="la-notes" className="mt-1" />
            </div>

            <div className={"rounded-xl p-4 text-sm space-y-1 " + (pressure ? "bg-amber-50 border border-amber-200" : "bg-[#F1EDE2]")}>
                <Row label="Weekly housing cost" value={`$${housingWk.toFixed(2)}`} />
                {householdWeekly > 0 && <Row label="Housing as % of household income" value={`${(ratio * 100).toFixed(1)}%`} bold />}
                {pressure && <div className="pt-1 text-amber-800 text-[12.5px]">
                    Housing pressure may be high — we&apos;ll review this in your plan.
                </div>}
            </div>

            <SaveBtn saving={saving} testId="la-save" />
        </form>
    );
}

// ---------------- Section 5: Assets ----------------
const ASSET_CATEGORIES = [
    "Family home", "Investment property or holiday home", "Household contents", "Vehicles including boats",
    "Bank accounts", "Shares", "Term deposits", "Superannuation", "Other investments",
    "Life insurance policies", "Other assets",
];

function AssetsSection({ intake, onSave, saving }) {
    const [items, setItems] = useState(intake?.assets?.items || []);
    function add() { setItems([...items, { category: ASSET_CATEGORIES[4], value: "", owned_by: "joint", accessible_cash: true, notes: "" }]); }
    function update(i, patch) { setItems(items.map((it, idx) => idx === i ? { ...it, ...patch } : it)); }
    function remove(i) { setItems(items.filter((_, idx) => idx !== i)); }

    const totals = useMemo(() => {
        const t = items.reduce((s, a) => s + (Number(a.value) || 0), 0);
        const c = items.filter((a) => a.accessible_cash).reduce((s, a) => s + (Number(a.value) || 0), 0);
        return { total: t, cash: c };
    }, [items]);

    async function submit(e) {
        e.preventDefault();
        await onSave({ items: items.map((a) => ({ ...a, value: Number(a.value) || 0 })) });
    }

    return (
        <form onSubmit={submit} className="space-y-4">
            <div className="flex items-center justify-between">
                <p className="text-sm text-[#5E636E]">Add each asset — property, vehicles, bank accounts, super, etc.</p>
                <Button type="button" size="sm" variant="outline" onClick={add} className="gap-1.5" data-testid="assets-add">
                    <Plus className="h-3.5 w-3.5" /> Add asset
                </Button>
            </div>
            {items.length === 0 && <div className="text-sm text-[#5E636E] italic">No assets added yet.</div>}
            <div className="space-y-3">
                {items.map((a, i) => (
                    <div key={i} className="rounded-xl border border-[#EAE7DE] p-3 space-y-2 relative">
                        <button type="button" className="absolute top-2 right-2 text-[#B7B3A6] hover:text-[#0F1115]" onClick={() => remove(i)} aria-label="Remove"><X className="h-4 w-4" /></button>
                        <div className="grid grid-cols-2 gap-3">
                            <SelectField label="Category" value={a.category} onChange={(v) => update(i, { category: v })} options={ASSET_CATEGORIES} testId={`asset-${i}-cat`} />
                            <Field label="Estimated value (NZD)" type="number" value={a.value} onChange={(v) => update(i, { value: v })} testId={`asset-${i}-val`} />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <SelectField label="Owned by" value={a.owned_by} onChange={(v) => update(i, { owned_by: v })} options={["client", "partner", "joint"]} testId={`asset-${i}-own`} />
                            <SelectField label="Accessible cash?" value={a.accessible_cash ? "Yes" : "No"} onChange={(v) => update(i, { accessible_cash: v === "Yes" })} options={["Yes", "No"]} testId={`asset-${i}-cash`} />
                        </div>
                        <Field label="Notes" value={a.notes || ""} onChange={(v) => update(i, { notes: v })} testId={`asset-${i}-notes`} />
                    </div>
                ))}
            </div>

            <div className="rounded-xl bg-[#F1EDE2] p-4 text-sm space-y-1">
                <Row label="Total household assets" value={`$${totals.total.toLocaleString()}`} bold />
                <Row label="Accessible cash" value={`$${totals.cash.toLocaleString()}`} />
            </div>

            <SaveBtn saving={saving} testId="assets-save" />
        </form>
    );
}

// ---------------- Section 6: Living costs ----------------
const LC_CATEGORIES = [
    { v: "housing_utilities", l: "Housing & utilities", items: ["Land rates", "Water", "Electricity", "Gas", "Home maintenance", "Phone", "Internet"] },
    { v: "food_essentials", l: "Food & essentials", items: ["Food & groceries", "Basic clothing", "Pharmacy & medication", "Doctor / dentist / optician"] },
    { v: "insurance", l: "Insurance", items: ["House & contents", "Life insurance", "Medical insurance", "Vehicle / boat insurance"] },
    { v: "transport", l: "Transport", items: ["Petrol / RUC", "Public transport", "Taxi / rideshare", "Rego & WOF", "Vehicle maintenance", "AA / roadside"] },
    { v: "children", l: "Children & dependants", items: ["Children's education", "Children's clothing", "Child maintenance", "Child care", "Other dependant costs"] },
    { v: "lifestyle", l: "Lifestyle & discretionary", items: ["TV / streaming", "Memberships", "Dining out", "Takeaways", "Shopping", "Personal spending", "Gifts", "Holidays / travel savings", "Other lifestyle"] },
];
const LC_CATS_FLAT = LC_CATEGORIES.map((c) => c.v);
const LC_TAGS = [
    { v: "essential", l: "Essential" },
    { v: "lifestyle", l: "Lifestyle" },
    { v: "review", l: "Review" },
];

function LivingCostsSection({ intake, onSave, saving }) {
    const [items, setItems] = useState(intake?.living_costs?.items || []);
    const [pickerCat, setPickerCat] = useState(LC_CATEGORIES[0].v);

    function addFromPreset(name, category) {
        setItems((prev) => [...prev, { name, category, balance: null, weekly: "", arrears: "", tag: category === "lifestyle" ? "lifestyle" : "essential", evidence_uploaded: false, notes: "" }]);
    }
    function addCustom() {
        setItems((prev) => [...prev, { name: "", category: pickerCat, balance: null, weekly: "", arrears: "", tag: "essential", evidence_uploaded: false, notes: "" }]);
    }
    function update(i, patch) { setItems(items.map((it, idx) => idx === i ? { ...it, ...patch } : it)); }
    function remove(i) { setItems(items.filter((_, idx) => idx !== i)); }

    const totals = useMemo(() => {
        const ess = items.filter((i) => i.tag === "essential").reduce((s, i) => s + (Number(i.weekly) || 0), 0);
        const life = items.filter((i) => i.tag === "lifestyle").reduce((s, i) => s + (Number(i.weekly) || 0), 0);
        const rev = items.filter((i) => i.tag === "review").reduce((s, i) => s + (Number(i.weekly) || 0), 0);
        const arr = items.reduce((s, i) => s + (Number(i.arrears) || 0), 0);
        return { ess, life, rev, total: ess + life + rev, arrears: arr };
    }, [items]);

    async function submit(e) {
        e.preventDefault();
        await onSave({ items: items.map((it) => ({
            ...it,
            weekly: Number(it.weekly) || 0,
            arrears: Number(it.arrears) || 0,
            balance: it.balance === "" || it.balance == null ? null : Number(it.balance),
        })) });
    }

    const presetsForCat = LC_CATEGORIES.find((c) => c.v === pickerCat)?.items || [];

    return (
        <form onSubmit={submit} className="space-y-4">
            <p className="text-sm text-[#5E636E]">
                Add every regular weekly cost. Tag each as <b>Essential</b> (must pay), <b>Lifestyle</b> (nice to have),
                or <b>Review</b> (worth revisiting during plan prep). No judgement here — accuracy matters more than the labels.
            </p>

            {/* Quick add from presets */}
            <div className="rounded-xl border border-dashed border-[#DCD8CC] p-3 space-y-2">
                <div className="flex items-center gap-2 flex-wrap">
                    <SelectField label="Category" value={pickerCat} onChange={setPickerCat} options={LC_CATEGORIES.map((c) => c.v)} testId="lc-picker-cat" />
                    <Button type="button" size="sm" variant="outline" onClick={addCustom} className="gap-1.5" data-testid="lc-add-custom">
                        <Plus className="h-3.5 w-3.5" /> Add blank row
                    </Button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                    {presetsForCat.map((preset) => (
                        <button
                            key={preset}
                            type="button"
                            onClick={() => addFromPreset(preset, pickerCat)}
                            className="rounded-full border border-[#EAE7DE] px-2.5 py-1 text-[12px] hover:bg-[#F1EDE2]"
                            data-testid={`lc-preset-${preset}`}
                        >
                            + {preset}
                        </button>
                    ))}
                </div>
            </div>

            {items.length === 0 && <div className="text-sm text-[#5E636E] italic">No living costs added yet — add a category above.</div>}
            <div className="space-y-2">
                {items.map((it, i) => (
                    <div key={i} className="rounded-xl border border-[#EAE7DE] p-3 grid gap-2">
                        <div className="grid grid-cols-[1fr_140px_140px_28px] gap-2 items-end">
                            <Field label={i === 0 ? "Item" : ""} value={it.name} onChange={(v) => update(i, { name: v })} testId={`lc-${i}-name`} />
                            <Field label={i === 0 ? "Weekly (NZD)" : ""} type="number" value={it.weekly} onChange={(v) => update(i, { weekly: v })} testId={`lc-${i}-wk`} />
                            <Field label={i === 0 ? "Arrears" : ""} type="number" value={it.arrears} onChange={(v) => update(i, { arrears: v })} testId={`lc-${i}-arr`} />
                            <button type="button" onClick={() => remove(i)} className="h-9 text-[#B7B3A6] hover:text-[#0F1115]" aria-label="Remove"><X className="h-4 w-4" /></button>
                        </div>
                        <div className="grid grid-cols-[1fr_1fr] gap-2">
                            <SelectField value={it.category} onChange={(v) => update(i, { category: v })} options={LC_CATS_FLAT} testId={`lc-${i}-cat`} />
                            <SelectField value={it.tag} onChange={(v) => update(i, { tag: v })} options={LC_TAGS.map((t) => t.v)} testId={`lc-${i}-tag`} />
                        </div>
                    </div>
                ))}
            </div>

            <div className="rounded-xl bg-[#F1EDE2] p-4 text-sm space-y-1">
                <Row label="Essential weekly" value={`$${totals.ess.toFixed(2)}`} />
                <Row label="Lifestyle weekly" value={`$${totals.life.toFixed(2)}`} />
                {totals.rev > 0 && <Row label="Review weekly" value={`$${totals.rev.toFixed(2)}`} />}
                <Row label="Total weekly" value={`$${totals.total.toFixed(2)}`} bold />
                {totals.arrears > 0 && <Row label="Arrears" value={`$${totals.arrears.toFixed(2)}`} />}
            </div>

            <SaveBtn saving={saving} testId="lc-save" />
        </form>
    );
}

// ---------------- Section 7: Debts ----------------
const DEBT_TYPES = [
    "Credit card", "Store card", "Personal loan", "Hire purchase", "Finance company loan",
    "Bank overdraft", "Car loan", "Buy Now Pay Later", "Tax debt", "Family or personal debt", "Other",
];
const REPAYMENT_METHODS = ["", "snowball", "avalanche", "hybrid", "manual"];

function DebtsSection({ intake, onSave, saving }) {
    const [items, setItems] = useState(intake?.debts?.items || []);
    const [method, setMethod] = useState(intake?.debts?.repayment_method || "");
    const [noDebts, setNoDebts] = useState(!!intake?.debts?.no_debts && (intake?.debts?.items?.length || 0) === 0);

    function add() {
        setItems([...items, { provider: "", type: DEBT_TYPES[0], balance: "", interest_rate: "", min_weekly: "", actual_weekly: "", arrears: "", due_date: "", secured: false, statement_uploaded: false, notes: "" }]);
        setNoDebts(false);
    }
    function update(i, patch) { setItems(items.map((it, idx) => idx === i ? { ...it, ...patch } : it)); }
    function remove(i) { setItems(items.filter((_, idx) => idx !== i)); }

    const householdWeekly = intake?.derived?.income?.household_weekly || 0;
    const totals = useMemo(() => {
        const t = items.reduce((s, d) => s + (Number(d.balance) || 0), 0);
        const wk = items.reduce((s, d) => s + (Number(d.actual_weekly) || 0), 0);
        const arr = items.reduce((s, d) => s + (Number(d.arrears) || 0), 0);
        const nonZero = items.filter((d) => Number(d.balance) > 0);
        const hi = nonZero.length ? nonZero.reduce((a, b) => Number(a.interest_rate || 0) >= Number(b.interest_rate || 0) ? a : b) : null;
        const sm = nonZero.length ? nonZero.reduce((a, b) => Number(a.balance || 0) <= Number(b.balance || 0) ? a : b) : null;
        const dti = householdWeekly > 0 ? t / (householdWeekly * 52) : null;
        const pressure = householdWeekly > 0 ? Math.min(100, Math.round((wk / householdWeekly) * 250)) : null;
        return { total: t, weekly: wk, arrears: arr, hi, sm, dti, pressure };
    }, [items, householdWeekly]);

    async function submit(e) {
        e.preventDefault();
        const payload = {
            items: noDebts ? [] : items.map((d) => ({
                ...d,
                balance: Number(d.balance) || 0,
                interest_rate: Number(d.interest_rate) || 0,
                min_weekly: Number(d.min_weekly) || 0,
                actual_weekly: Number(d.actual_weekly) || 0,
                arrears: Number(d.arrears) || 0,
            })),
            repayment_method: method || null,
        };
        await onSave(payload);
    }

    return (
        <form onSubmit={submit} className="space-y-4">
            <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={noDebts} onChange={(e) => { setNoDebts(e.target.checked); if (e.target.checked) setItems([]); }} data-testid="debt-none" />
                Our household has no debt right now
            </label>

            {!noDebts && (
                <>
                    <div className="flex items-center justify-between">
                        <p className="text-sm text-[#5E636E]">Add every current debt — credit cards, personal loans, car finance, BNPL, tax debt, family loans.</p>
                        <Button type="button" size="sm" variant="outline" onClick={add} className="gap-1.5" data-testid="debt-add">
                            <Plus className="h-3.5 w-3.5" /> Add debt
                        </Button>
                    </div>
                    <div className="space-y-3">
                        {items.map((d, i) => (
                            <div key={i} className="rounded-xl border border-[#EAE7DE] p-3 space-y-2 relative">
                                <button type="button" className="absolute top-2 right-2 text-[#B7B3A6] hover:text-[#0F1115]" onClick={() => remove(i)} aria-label="Remove"><X className="h-4 w-4" /></button>
                                <div className="grid grid-cols-2 gap-3">
                                    <Field label="Provider" value={d.provider} onChange={(v) => update(i, { provider: v })} testId={`debt-${i}-prov`} />
                                    <SelectField label="Type" value={d.type} onChange={(v) => update(i, { type: v })} options={DEBT_TYPES} testId={`debt-${i}-type`} />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <Field label="Current balance (NZD)" type="number" value={d.balance} onChange={(v) => update(i, { balance: v })} testId={`debt-${i}-bal`} />
                                    <Field label="Interest rate (% APR)" type="number" value={d.interest_rate} onChange={(v) => update(i, { interest_rate: v })} testId={`debt-${i}-int`} />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <Field label="Min payment / week" type="number" value={d.min_weekly} onChange={(v) => update(i, { min_weekly: v })} testId={`debt-${i}-min`} />
                                    <Field label="Actual payment / week" type="number" value={d.actual_weekly} onChange={(v) => update(i, { actual_weekly: v })} testId={`debt-${i}-actual`} />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <Field label="Arrears" type="number" value={d.arrears} onChange={(v) => update(i, { arrears: v })} testId={`debt-${i}-arr`} />
                                    <Field label="Due date" type="date" value={d.due_date} onChange={(v) => update(i, { due_date: v })} testId={`debt-${i}-due`} />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <SelectField label="Secured?" value={d.secured ? "Yes" : "No"} onChange={(v) => update(i, { secured: v === "Yes" })} options={["No", "Yes"]} testId={`debt-${i}-sec`} />
                                    <SelectField label="Statement uploaded?" value={d.statement_uploaded ? "Yes" : "No"} onChange={(v) => update(i, { statement_uploaded: v === "Yes" })} options={["No", "Yes"]} testId={`debt-${i}-stmt`} />
                                </div>
                            </div>
                        ))}
                    </div>
                    <SelectField label="Preferred repayment method (optional — your CFO will confirm)" value={method} onChange={setMethod} options={REPAYMENT_METHODS} testId="debt-method" />

                    <div className="rounded-xl bg-[#F1EDE2] p-4 text-sm space-y-1">
                        <Row label="Total household debt" value={`$${totals.total.toLocaleString()}`} bold />
                        <Row label="Total weekly debt payments" value={`$${totals.weekly.toFixed(2)}`} />
                        {totals.arrears > 0 && <Row label="Debt arrears" value={`$${totals.arrears.toFixed(2)}`} />}
                        {totals.hi && <Row label="Highest interest" value={`${totals.hi.provider || "—"} · ${Number(totals.hi.interest_rate || 0).toFixed(1)}%`} />}
                        {totals.sm && <Row label="Smallest balance" value={`${totals.sm.provider || "—"} · $${Number(totals.sm.balance || 0).toLocaleString()}`} />}
                        {totals.dti !== null && <Row label="Debt-to-income" value={`${(totals.dti * 100).toFixed(1)}%`} />}
                        {totals.pressure !== null && <Row label="Debt pressure score" value={`${totals.pressure} / 100`} />}
                    </div>
                </>
            )}

            <SaveBtn saving={saving} testId="debt-save" />
        </form>
    );
}

// ---------------- Section 8: Arrears ----------------
const PRIORITY_LEVELS = [
    { v: "critical", l: "Critical", cls: "bg-red-100 text-red-800" },
    { v: "high", l: "High", cls: "bg-amber-100 text-amber-800" },
    { v: "medium", l: "Medium", cls: "bg-yellow-100 text-yellow-800" },
    { v: "low", l: "Low", cls: "bg-slate-100 text-slate-700" },
];

function ArrearsSection({ intake, onSave, saving }) {
    const [items, setItems] = useState(intake?.arrears?.other_items || []);

    // Auto-aggregated arrears from other sections (for display / awareness only)
    const auto = useMemo(() => {
        const rows = [];
        const la = intake?.living_arrangements;
        if (la?.arrears > 0) rows.push({ source: "Housing", provider: la.provider_name || "Housing", amount: la.arrears });
        (intake?.living_costs?.items || []).filter((i) => Number(i.arrears || 0) > 0).forEach((i) => rows.push({ source: "Living costs", provider: i.name, amount: Number(i.arrears || 0) }));
        (intake?.debts?.items || []).filter((d) => Number(d.arrears || 0) > 0).forEach((d) => rows.push({ source: "Debt", provider: d.provider || d.type, amount: Number(d.arrears || 0) }));
        return rows;
    }, [intake]);
    const autoTotal = auto.reduce((s, r) => s + r.amount, 0);
    const otherTotal = items.reduce((s, r) => s + (Number(r.amount_overdue) || 0), 0);
    const criticalTotal = items.filter((r) => r.priority === "critical").reduce((s, r) => s + (Number(r.amount_overdue) || 0), 0);

    function add() { setItems([...items, { provider: "", amount_overdue: "", due_date: "", consequence: "", priority: "high", notes: "" }]); }
    function update(i, patch) { setItems(items.map((it, idx) => idx === i ? { ...it, ...patch } : it)); }
    function remove(i) { setItems(items.filter((_, idx) => idx !== i)); }

    async function submit(e) {
        e.preventDefault();
        await onSave({
            other_items: items.map((r) => ({ ...r, amount_overdue: Number(r.amount_overdue) || 0 })),
            reviewed: true,
        });
    }

    return (
        <form onSubmit={submit} className="space-y-4">
            <p className="text-sm text-[#5E636E]">
                We&apos;ve already picked up arrears you entered in earlier sections. Add anything else that&apos;s overdue —
                fines, IRD, school fees, subscriptions — so your CFO can help you prioritise.
            </p>

            {auto.length > 0 && (
                <div className="rounded-xl border border-[#EAE7DE] bg-[#FBFAF6] p-3">
                    <div className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Auto-collected from earlier sections</div>
                    <ul className="mt-2 space-y-1 text-sm">
                        {auto.map((r, i) => (
                            <li key={i} className="flex items-center justify-between">
                                <span><span className="text-[#5E636E] text-[11px] uppercase mr-2">{r.source}</span>{r.provider}</span>
                                <span className="tabular-nums">${r.amount.toFixed(2)}</span>
                            </li>
                        ))}
                        <li className="flex items-center justify-between pt-2 border-t border-[#EAE7DE] font-semibold">
                            <span>Auto subtotal</span>
                            <span className="tabular-nums">${autoTotal.toFixed(2)}</span>
                        </li>
                    </ul>
                </div>
            )}

            <div className="flex items-center justify-between">
                <div className="text-[13px] font-semibold">Other overdue bills</div>
                <Button type="button" size="sm" variant="outline" onClick={add} className="gap-1.5" data-testid="arr-add"><Plus className="h-3.5 w-3.5" /> Add overdue bill</Button>
            </div>

            {items.length === 0 && <div className="text-sm text-[#5E636E] italic">No extra overdue bills added.</div>}
            <div className="space-y-3">
                {items.map((r, i) => (
                    <div key={i} className="rounded-xl border border-[#EAE7DE] p-3 space-y-2 relative">
                        <button type="button" className="absolute top-2 right-2 text-[#B7B3A6] hover:text-[#0F1115]" onClick={() => remove(i)} aria-label="Remove"><X className="h-4 w-4" /></button>
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Provider" value={r.provider} onChange={(v) => update(i, { provider: v })} testId={`arr-${i}-prov`} />
                            <Field label="Amount overdue" type="number" value={r.amount_overdue} onChange={(v) => update(i, { amount_overdue: v })} testId={`arr-${i}-amt`} />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Due date" type="date" value={r.due_date} onChange={(v) => update(i, { due_date: v })} testId={`arr-${i}-due`} />
                            <SelectField label="Priority" value={r.priority} onChange={(v) => update(i, { priority: v })} options={PRIORITY_LEVELS.map((p) => p.v)} testId={`arr-${i}-pri`} />
                        </div>
                        <Field label="Consequence if unpaid" value={r.consequence || ""} onChange={(v) => update(i, { consequence: v })} testId={`arr-${i}-cons`} />
                    </div>
                ))}
            </div>

            <div className="rounded-xl bg-[#F1EDE2] p-4 text-sm space-y-1">
                <Row label="Auto-collected arrears" value={`$${autoTotal.toFixed(2)}`} />
                <Row label="Other overdue bills" value={`$${otherTotal.toFixed(2)}`} />
                <Row label="Critical arrears" value={`$${criticalTotal.toFixed(2)}`} />
                <Row label="Total arrears" value={`$${(autoTotal + otherTotal).toFixed(2)}`} bold />
            </div>

            <SaveBtn saving={saving} testId="arr-save" />
        </form>
    );
}

// ---------------- Shared save button ----------------
function SaveBtn({ saving, testId }) {
    return (
        <div className="pt-1">
            <Button type="submit" disabled={saving} className="bg-[#0F1115] hover:bg-[#2E2E2E] text-white gap-2" data-testid={testId}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {saving ? "Saving…" : "Save section"}
            </Button>
        </div>
    );
}
